/**
 * Which stored artwork variant to render, and at what size.
 *
 * Releases carry three: `thumbnail` (50×50, ~5 KB), `low` (250×250, ~80 KB)
 * and `original` (the master, which on a real release runs to several MB).
 * Every card surface in the shop reached for `thumbnail` first — so a 50×50
 * image was being upscaled into tiles 150–400 px wide, everywhere, and looked
 * exactly as soft as that sounds.
 *
 * Two orderings, because the right answer depends on how big the thing is:
 *
 *   CARD  low → thumbnail → original.  `original` is deliberately LAST: a
 *         blurry 50×50 beats a multi-megabyte download for a tile, so it is
 *         only reached when neither derived size exists.
 *   ROW   thumbnail → low → original.  A 40 px row genuinely wants the 5 KB
 *         file; sixty history rows at 80 KB each is 4.8 MB for artwork nobody
 *         can see the detail in.
 *
 * Both proxy through /api/assets/serve, which signs with the owning tenant's
 * credentials — object storage is not uniformly public across tenants, and a
 * raw URL renders as a broken image for the ones whose bucket refuses
 * anonymous reads.
 */

function proxy(url: string | null | undefined): string | null {
    if (!url || typeof url !== 'string') return null
    if (!/^https?:\/\//i.test(url)) return url
    return `/api/assets/serve?url=${encodeURIComponent(url.split('?')[0]!)}`
}

/** For tiles, covers and the player's large artwork. */
export function cardArtworkUrl(art: any): string | null {
    return proxy(art?.low?.url || art?.thumbnail?.url || art?.original?.url || art?.url || null)
}

/** For narrow list rows, where the 50×50 is the correct file. */
export function rowArtworkUrl(art: any): string | null {
    return proxy(art?.thumbnail?.url || art?.low?.url || art?.original?.url || art?.url || null)
}
